'use client';

/**
 * Generate Media Form Component - Generation Studio
 *
 * Two-column layout for generating AI images and videos.
 * Left column: Controls (model selection, prompt, presets, tips)
 * Right column: Gallery (generated media with Images/Videos tabs)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Coins,
  Film,
  Image,
  Loader2,
  Sparkles,
  User,
  Users,
  Wand2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@kit/supabase/hooks/use-user';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { GradientTitle } from '~/components/gradient-title';
import { useCoinBalance, useInvalidateCoinBalance } from '~/components/wallet';
import { useBuyCoinsModal } from '~/components/wallet/buy-coins-modal-context';
import { safeSessionStorage } from '~/lib/safe-storage';
import { generateMedia } from '~/lib/server/actions/generate-actions';
import {
  generateImagePrompt,
  getCharacterBasePrompt,
} from '~/lib/server/actions/prompt-generation-actions';

import { GenerationGallery } from './generation-gallery';
import { ModelSelectionModal } from './model-selection-modal';
import { type PresetItem, QuickPresets } from './quick-presets';
import { TipsDropdown } from './tips-dropdown';
import {
  ASPECT_RATIO_LABELS,
  type AspectRatio,
  type CharacterOption,
  DEFAULT_GENERATE_STATE,
  type GenerateMediaState,
  type MediaType,
  type QualityPreset,
  TOKEN_COSTS,
  VIDEO_DURATION_OPTIONS,
  videoDurationOption,
} from './types';

const MAX_PROMPT_LENGTH = 500;

export function GenerateMediaForm() {
  const { t } = useTranslation('common');
  const [state, setState] = useState<GenerateMediaState>(
    DEFAULT_GENERATE_STATE,
  );
  const [selectedCharacter, setSelectedCharacter] =
    useState<CharacterOption | null>(null);
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Read pre-selected character from sessionStorage (set by My Characters page)
  useEffect(() => {
    const stored = safeSessionStorage.getItem('selectedCharacter');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const charOption: CharacterOption = {
          id: parsed.id,
          name: parsed.name,
          slug: parsed.slug,
          avatarUrl: parsed.avatarUrl || '',
          style: parsed.style || 'realistic',
          isUserCreated: true,
        };
        setSelectedCharacter(charOption);
        setState((prev) => ({ ...prev, selectedCharacterId: parsed.id }));
      } catch {
        // Invalid data in sessionStorage, ignore
      }
      safeSessionStorage.removeItem('selectedCharacter');
    }
  }, []);

  const { data: user, isLoading: isLoadingUser } = useUser();
  const { openAuthModal } = useAuthModal();
  const { openBuyCoinsModal } = useBuyCoinsModal();
  const isAuthenticated = !!user;

  const { data, isLoading: loadingBalance } = useCoinBalance();
  const invalidateBalance = useInvalidateCoinBalance();
  const userTokens = data?.coins ?? null;

  const [generationResult, setGenerationResult] = useState<{
    success: boolean;
    message: string;
    generationId?: string;
  } | null>(null);

  const updateState = (updates: Partial<GenerateMediaState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const durationOption = videoDurationOption(state.videoDurationSeconds);
  const currentCost =
    state.mediaType === 'video'
      ? durationOption.coins
      : TOKEN_COSTS[state.mediaType][state.quality];
  const hasEnoughTokens = userTokens !== null && userTokens >= currentCost;
  // The button stays CLICKABLE unless we're genuinely mid-flight. We no longer
  // pre-disable it for an empty prompt or low balance — a disabled button fires
  // no onClick, so those cases used to read as "Generate does nothing" and the
  // user just left. Instead `handleGenerate` runs and gives explicit feedback
  // (prompt hint / open the buy-coins modal). See the click handler below.
  const isBusy =
    isLoadingUser || state.isGenerating || (isAuthenticated && loadingBalance);

  const handleModelSelect = (character: CharacterOption) => {
    setSelectedCharacter(character);
    updateState({ selectedCharacterId: character.id });
  };

  const handlePresetSelect = (preset: PresetItem | null) => {
    if (preset === null) {
      // Deselecting - clear the preset ID but keep the prompt
      setSelectedPresetId(null);
    } else {
      setSelectedPresetId(preset.id);
      updateState({ prompt: preset.prompt });
    }
  };

  const handleGenerateAIPrompt = async () => {
    if (isGeneratingPrompt) return;

    // The AI prompt is tailored to a character's style/base prompt. Instead
    // of a silently-disabled button, guide the user into picking a model.
    if (!selectedCharacter) {
      toast.error(t('generate.aiPromptNeedsModel'));
      setIsModelModalOpen(true);
      return;
    }

    setIsGeneratingPrompt(true);

    try {
      // Get character's base generation prompt and style
      const basePromptResult = await getCharacterBasePrompt(
        selectedCharacter.id,
      );

      const result = await generateImagePrompt(
        state.prompt || 'casual pose, looking at camera',
        {
          name: selectedCharacter.name,
          baseGenerationPrompt: basePromptResult.success
            ? basePromptResult.basePrompt
            : undefined,
          style: basePromptResult.success
            ? basePromptResult.style
            : selectedCharacter.style,
        },
      );

      if (result.success && result.prompt) {
        updateState({
          prompt: result.prompt,
          negativePrompt: result.negativePrompt || state.negativePrompt,
        });
        setSelectedPresetId(null); // Clear preset selection since we have a custom prompt
      } else {
        toast.error(result.error || t('generate.aiPromptFailed'));
      }
    } catch (error) {
      console.error('Failed to generate prompt:', error);
      toast.error(t('generate.aiPromptFailed'));
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const handleGenerate = async () => {
    // Show auth modal if user is not authenticated
    if (!isAuthenticated) {
      openAuthModal('sign-up', '/generate');
      return;
    }

    // Still loading the user/balance — ignore the click silently.
    if (isBusy) return;

    // Explicit feedback instead of a dead disabled button:
    // 1) no prompt yet → tell them to describe the scene.
    if (!state.prompt.trim()) {
      toast.error(t('generate.promptRequired'));
      promptRef.current?.focus();
      promptRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    // 2) not enough coins → open the buy-coins modal (this branch was
    //    previously unreachable because the button was pre-disabled).
    if (!hasEnoughTokens) {
      openBuyCoinsModal();
      return;
    }

    updateState({ isGenerating: true });
    setGenerationResult(null);

    try {
      const result = await generateMedia({
        characterId: state.selectedCharacterId || undefined,
        prompt: state.prompt,
        negativePrompt: state.negativePrompt || undefined,
        mediaType: state.mediaType,
        aspectRatio: state.aspectRatio,
        quality: state.quality,
        durationSeconds:
          state.mediaType === 'video' ? state.videoDurationSeconds : undefined,
      });

      if (result.success) {
        invalidateBalance();
        setGenerationResult({
          success: true,
          message: t('generate.generationStarted', { cost: result.cost }),
          generationId: result.generationId,
        });
        // Trigger gallery refresh
        setRefreshTrigger((prev) => prev + 1);
      } else {
        if (result.error === 'Not authenticated') {
          // Import dynamically to avoid circular deps since this file is large
          const { fireAuthExpiredEvent } = await import('~/lib/auth-error');
          fireAuthExpiredEvent();
        } else if (result.error?.includes('Insufficient') || result.required) {
          openBuyCoinsModal();
        } else {
          setGenerationResult({
            success: false,
            message: result.error ?? t('generate.generationFailed'),
          });
        }
      }
    } catch (error) {
      console.error('Generation failed:', error);
      setGenerationResult({
        success: false,
        message: t('generate.unexpectedError'),
      });
    } finally {
      updateState({ isGenerating: false });
    }
  };

  return (
    <div className="relative min-h-full overflow-hidden">
      {/* Background Gradient Orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-1/4 h-96 w-96 rounded-full bg-purple-600/5 blur-3xl" />
        <div className="absolute bottom-20 left-1/3 h-80 w-80 rounded-full bg-pink-600/5 blur-3xl" />
        <div className="absolute top-1/2 right-1/3 h-72 w-72 rounded-full bg-purple-600/5 blur-3xl" />
      </div>

      <div className="relative z-10 min-h-full px-3 py-4 sm:px-4 sm:py-6">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-4 sm:mb-6">
            <h1 className="mb-1 text-2xl font-bold sm:text-3xl">
              <GradientTitle i18nKey="generate.title" />
            </h1>
            <p className="text-sm text-gray-400 sm:text-base">
              <Trans i18nKey="common:generate.subtitle" />
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 gap-3 sm:gap-6 md:grid-cols-[320px_1fr] lg:grid-cols-[380px_1fr]">
            {/* Left Column - Controls */}
            <div className="space-y-4">
              {/* Model Selection Button */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <label className="mb-2 block text-sm text-gray-400">
                  <Trans i18nKey="common:generate.selectModel" />
                </label>
                <button
                  onClick={() => setIsModelModalOpen(true)}
                  className="hover:border-eros-pink/50 group flex w-full items-center gap-3 rounded-xl border border-white/10 bg-black/30 p-3 transition-all"
                >
                  {selectedCharacter ? (
                    <>
                      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg">
                        <img
                          src={selectedCharacter.avatarUrl}
                          alt={selectedCharacter.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-white">
                          {selectedCharacter.name}
                        </p>
                        <p className="text-sm text-gray-400 capitalize">
                          {selectedCharacter.style}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-white/5">
                        <Sparkles className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-gray-400">
                          <Trans i18nKey="common:generate.selectModelEmpty" />
                        </p>
                        <p className="text-sm text-gray-500">
                          <Trans i18nKey="common:generate.selectModelHint" />
                        </p>
                      </div>
                    </>
                  )}
                  <ChevronDown className="group-hover:text-eros-pink h-5 w-5 text-gray-500 transition-colors" />
                </button>
              </div>

              {/* Solo/Group Toggle - Under Development */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 opacity-60">
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm text-gray-400">
                    <Trans i18nKey="common:generate.mode" />
                  </label>
                  <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-400">
                    <Trans i18nKey="common:generate.underDevelopment" />
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    disabled
                    className="from-eros-pink to-eros-purple flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-gradient-to-r py-2.5 text-sm font-medium text-white"
                  >
                    <User className="h-4 w-4" />
                    <Trans i18nKey="common:generate.modeSolo" />
                  </button>
                  <button
                    disabled
                    className="flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-white/5 py-2.5 text-sm font-medium text-gray-400"
                  >
                    <Users className="h-4 w-4" />
                    <Trans i18nKey="common:generate.modeGroup" />
                  </button>
                </div>
              </div>

              {/* Prompt Input */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm text-gray-400">
                    <Trans i18nKey="common:generate.prompt" />
                  </label>
                  <span
                    className={`text-xs ${state.prompt.length > MAX_PROMPT_LENGTH ? 'text-red-400' : 'text-gray-500'}`}
                  >
                    {state.prompt.length}/{MAX_PROMPT_LENGTH}
                  </span>
                </div>
                <div className="relative">
                  <textarea
                    ref={promptRef}
                    value={state.prompt}
                    onChange={(e) =>
                      updateState({
                        prompt: e.target.value.slice(0, MAX_PROMPT_LENGTH),
                      })
                    }
                    placeholder={t('generate.promptPlaceholder')}
                    rows={4}
                    className="focus:border-eros-pink/50 w-full resize-none rounded-xl border border-white/10 bg-black/30 px-4 py-3 pr-12 text-white placeholder-gray-500 transition-all focus:outline-none"
                  />
                  {/* AI Prompt Button (inside textarea) */}
                  <button
                    onClick={handleGenerateAIPrompt}
                    disabled={isGeneratingPrompt}
                    className="from-eros-pink to-eros-purple absolute right-3 bottom-3 rounded-lg bg-gradient-to-r p-2 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    title={t('generate.aiPromptTooltip')}
                  >
                    {isGeneratingPrompt ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Tips Dropdown */}
              <TipsDropdown />

              {/* Quick Presets */}
              <QuickPresets
                selectedPresetId={selectedPresetId}
                onSelectPreset={handlePresetSelect}
                isPremiumUser={true} // TODO: Get from user context
              />

              {/* Advanced Options (Collapsible) */}
              <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
                <button
                  onClick={() =>
                    updateState({ showAdvanced: !state.showAdvanced })
                  }
                  className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-white/5"
                >
                  <span className="font-medium text-white">
                    <Trans i18nKey="common:generate.advancedOptions" />
                  </span>
                  <motion.div
                    animate={{ rotate: state.showAdvanced ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {state.showAdvanced && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="space-y-4 px-4 pb-4">
                        {/* Media Type */}
                        <div>
                          <label className="mb-2 block text-sm text-gray-400">
                            <Trans i18nKey="common:generate.mediaType" />
                          </label>
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                updateState({ mediaType: 'image' })
                              }
                              className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-all ${
                                state.mediaType === 'image'
                                  ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <Image className="h-4 w-4" />
                              <Trans i18nKey="common:generate.mediaImage" />
                            </button>
                            <button
                              onClick={() =>
                                updateState({ mediaType: 'video' })
                              }
                              className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium transition-all ${
                                state.mediaType === 'video'
                                  ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <Film className="h-4 w-4" />
                              <Trans i18nKey="common:generate.mediaVideo" />
                            </button>
                          </div>
                        </div>

                        {/* Aspect Ratio */}
                        <div>
                          <label className="mb-2 block text-sm text-gray-400">
                            <Trans i18nKey="common:generate.aspectRatio" />
                          </label>
                          <div className="flex gap-2">
                            {(['9:16', '16:9', '1:1'] as AspectRatio[]).map(
                              (ratio) => (
                                <button
                                  key={ratio}
                                  onClick={() =>
                                    updateState({ aspectRatio: ratio })
                                  }
                                  className={`flex-1 rounded-lg py-2 text-sm font-medium transition-all ${
                                    state.aspectRatio === ratio
                                      ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                                  }`}
                                >
                                  {ratio}
                                </button>
                              ),
                            )}
                          </div>
                        </div>

                        {/* Video length — the H3 cost driver. Discrete stops:
                            the model only accepts 17n+5 frame counts, and cost
                            scales superlinearly (73f=60s, 124f=132s, 175f=313s). */}
                        {state.mediaType === 'video' && (
                          <div>
                            <div className="mb-2 flex items-baseline justify-between">
                              <label className="text-sm text-gray-400">
                                <Trans i18nKey="common:generate.videoLength" />
                              </label>
                              <span className="text-sm font-medium text-white">
                                {durationOption.seconds}s
                                <span className="ml-2 text-xs font-normal text-gray-400">
                                  {durationOption.waitLabel} ·{' '}
                                  {durationOption.coins} coins
                                </span>
                              </span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={VIDEO_DURATION_OPTIONS.length - 1}
                              step={1}
                              value={VIDEO_DURATION_OPTIONS.findIndex(
                                (o) => o.seconds === state.videoDurationSeconds,
                              )}
                              onChange={(e) =>
                                updateState({
                                  videoDurationSeconds:
                                    VIDEO_DURATION_OPTIONS[
                                      Number(e.target.value)
                                    ]!.seconds,
                                })
                              }
                              className="accent-eros-pink h-2 w-full cursor-pointer appearance-none rounded-lg bg-white/10"
                              data-test="video-duration-slider"
                            />
                            <div className="mt-1 flex justify-between text-[10px] text-gray-500">
                              {VIDEO_DURATION_OPTIONS.map((o) => (
                                <span key={o.seconds}>
                                  {o.seconds}s · {o.waitLabel}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Quality (images only) */}
                        {state.mediaType === 'image' && (
                          <div>
                            <label className="mb-2 block text-sm text-gray-400">
                              <Trans i18nKey="common:generate.quality" />
                            </label>
                            <div className="flex gap-2">
                              {(
                                [
                                  'standard',
                                  'high',
                                  'premium',
                                ] as QualityPreset[]
                              ).map((quality) => (
                                <button
                                  key={quality}
                                  onClick={() => updateState({ quality })}
                                  className={`flex-1 rounded-lg px-1 py-2 text-sm transition-all ${
                                    state.quality === quality
                                      ? 'from-eros-pink to-eros-purple bg-gradient-to-r text-white'
                                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                                  }`}
                                >
                                  <span>
                                    {t(
                                      `generate.quality${quality.charAt(0).toUpperCase()}${quality.slice(1)}`,
                                    )}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Negative Prompt */}
                        <div>
                          <label className="mb-2 block text-sm text-gray-400">
                            <Trans i18nKey="common:generate.negativePrompt" />
                          </label>
                          <textarea
                            value={state.negativePrompt}
                            onChange={(e) =>
                              updateState({ negativePrompt: e.target.value })
                            }
                            placeholder={t(
                              'generate.negativePromptPlaceholder',
                            )}
                            rows={2}
                            className="focus:border-eros-pink/50 w-full resize-none rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Generation Result Message */}
              <AnimatePresence>
                {generationResult && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`rounded-xl border p-4 ${
                      generationResult.success
                        ? 'border-green-500/30 bg-green-500/10 text-green-400'
                        : 'border-red-500/30 bg-red-500/10 text-red-400'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {generationResult.success ? (
                        <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                      )}
                      <span className="text-sm">
                        {generationResult.message}
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Generate Button */}
              <motion.button
                onClick={handleGenerate}
                disabled={isBusy}
                whileHover={!isBusy ? { scale: 1.02 } : {}}
                whileTap={!isBusy ? { scale: 0.98 } : {}}
                className={`flex w-full items-center justify-center gap-3 rounded-xl py-4 text-lg font-bold transition-all ${
                  !isBusy
                    ? 'bg-gradient-to-r from-red-600 via-pink-600 to-pink-700 text-white shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50'
                    : 'cursor-not-allowed bg-gray-700 text-gray-400'
                }`}
              >
                {state.isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <Trans i18nKey="common:generate.generating" />
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    <Trans i18nKey="common:generate.generateAction" />
                    <span className="flex items-center gap-1 text-sm opacity-80">
                      <Coins className="h-4 w-4" />
                      {currentCost}
                    </span>
                  </>
                )}
              </motion.button>

              {/* Balance/Error Messages */}
              {loadingBalance && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <Trans i18nKey="common:generate.loadingBalance" />
                </div>
              )}
              {!loadingBalance &&
                userTokens !== null &&
                userTokens < currentCost && (
                  <div className="flex items-center justify-center gap-2 text-sm text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    {t('generate.insufficientCoins', {
                      cost: currentCost,
                      have: userTokens,
                    })}
                  </div>
                )}
            </div>

            {/* Right Column - Gallery */}
            <div className="min-h-[400px] rounded-xl border border-white/10 bg-white/5 p-3 sm:min-h-[600px] sm:p-6">
              <GenerationGallery
                refreshTrigger={refreshTrigger}
                activeMediaType={state.mediaType}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Model Selection Modal */}
      <ModelSelectionModal
        isOpen={isModelModalOpen}
        onClose={() => setIsModelModalOpen(false)}
        selectedId={state.selectedCharacterId}
        onSelect={handleModelSelect}
      />
    </div>
  );
}
